import React, { useState } from "react";
import { Warning, X, CaretDown } from "@phosphor-icons/react";

export default function AlertBanner({ alerts }) {
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  if (!alerts || alerts.length === 0) return null;

  const severity = alerts.some((a) => /extreme|severe/i.test(a.severity || "")) ? "extreme" : "elevated";

  return (
    <div data-testid="alert-banner" className="relative border-b border-tac-border bg-gradient-to-r from-[#FFB347]/15 via-[#F2542D]/10 to-transparent">
      <button
        onClick={() => setOpen(!open)}
        data-testid="alert-banner-toggle"
        className="w-full flex items-center gap-3 px-4 sm:px-6 py-2.5 text-left"
      >
        <Warning size={18} weight="fill" className={severity === "extreme" ? "text-tac-danger" : "text-tac-warning"} />
        {!open && severity === "extreme" && (
          <span data-testid="alert-banner-pulse" className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-tac-danger opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-tac-danger" />
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-tac-warning">
              Active Weather Alerts
            </span>
            <span className="text-[15px] font-mono-tac tabular text-tac-dim">
              {alerts.length} {alerts.length === 1 ? "alert" : "alerts"}
            </span>
          </div>
          <div className="text-xs sm:text-sm text-tac-text font-semibold truncate">
            {alerts[0].event} — {alerts[0].headline}
          </div>
        </div>
        <CaretDown size={14} weight="bold" className={`text-tac-dim transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 sm:px-6 pb-3 max-h-80 overflow-y-auto tac-scroll">
          <div className="space-y-2">
            {alerts.map((a) => (
              <div key={a.id} data-testid={`alert-item-${a.id}`} className="border border-tac-border bg-tac-surface/60 rounded-sm">
                <button
                  onClick={() => setExpandedId(expandedId === a.id ? null : a.id)}
                  className="w-full text-left p-3 flex items-start gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-tac-warning">
                        {a.event}
                      </span>
                      {a.severity && (
                        <span className="text-[15px] font-mono-tac uppercase tracking-wider text-tac-dim">
                          · {a.severity}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-tac-text font-medium leading-snug">
                      {a.headline}
                    </div>
                    {a.area_desc && (
                      <div className="text-[15px] font-mono-tac text-tac-faint mt-1 truncate">
                        {a.area_desc}
                      </div>
                    )}
                    {a.zones?.length > 0 && (
                      <div className="text-[12px] font-mono-tac text-tac-dim mt-0.5" data-testid={`wx-zones-${a.id}`}>
                        Zone{a.zones.length === 1 ? "" : "s"}: {a.zones.join(" · ")}
                      </div>
                    )}
                  </div>
                  <CaretDown size={12} className={`text-tac-dim mt-1 transition-transform ${expandedId === a.id ? "rotate-180" : ""}`} />
                </button>
                {expandedId === a.id && a.description && (
                  <div className="px-3 pb-3 text-[15px] text-tac-dim whitespace-pre-line leading-relaxed border-t border-tac-border pt-2">
                    {a.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
